import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../../services/api';
import { useScenarioValidation } from '../../../hooks/useScenarioValidation';
import ValidationAlert, { FieldValidationError } from '../../Common/ValidationAlert';
import { calculateAggregateSum } from '../../../utils/scenarioValidation';
import './CreateScenarioWizard.css';

const getFieldLabel = (fieldKey) => {
  // EXTENDED - Enhanced field label mapping for new stages
  const labelMappings = {
    // === A1 QUARRY STAGE LABELS (preserved) ===
    'renewable_diesel_sandvik_1': 'Renewable Diesel Powered Sandvik 1',
    'renewable_diesel_sandvik_2': 'Renewable Diesel Powered Sandvik 2',
    'drilling_dust': 'Drilling Dust Emissions',
    
    // Blasting
    'rocks_blasted': 'Amount of Rocks Blasted',
    'amount_3_8_inch': 'Amount of 3/8" Aggregate',
    'amount_1_2_inch': 'Amount of 1/2" Aggregate',
    'amount_1_1_2_inch': 'Amount of 1-1/2" Aggregate',
    'amount_3_4_inch': 'Amount of 3/4" Aggregate',
    'amount_c33': 'Amount of C33 Aggregate',
    'subtek_eclipse': 'Subtek Eclipse (Explosive)',
    'blasting_dust': 'Blasting Dust Emissions',
    
    // Crushing & Conveying
    'rocks_to_crush': 'Rocks that will be Crushed',
    'electricity_primary_crusher_telsmith': 'Primary Crusher (Telsmith) - Electricity',
    'electricity_primary_crusher_lippman': 'Primary Crusher (Lippman) - Electricity',
    'electricity_secondary_crusher_telsmith': 'Secondary Crusher (Telsmith) - Electricity',
    'electricity_tertiary_crusher_telsmith': 'Tertiary Crusher (Telsmith) - Electricity',
    'electricity_quaternary_crusher_telsmith': 'Quaternary Crusher (Telsmith) - Electricity',
    'electricity_feeder_telsmith': 'Feeder (Telsmith) - Electricity',
    'electricity_feeder_lippman': 'Feeder (Lippman) - Electricity',
    'crushing_dust': 'Crushing Dust Emissions',
    
    // Processing/Screening
    'crushed_rock_processing': 'Crushed Rock being Processed',
    'electricity_scalping_screen_telsmith': 'Scalping Screen (Telsmith) - Electricity',
    'electricity_scalping_screen_simplicity': 'Scalping Screen (Simplicity) - Electricity',
    'electricity_tertiary_screen': 'Tertiary Screen - Electricity',
    'electricity_washscreen': 'Washscreen - Electricity',
    'electricity_dewatering_screen': 'Dewatering Screen - Electricity',
    'electricity_wash_tank': 'Wash Tank - Electricity',
    'electricity_fine_material_screw': 'Fine Material Screw - Electricity',
    'fuel_water_trucks': 'Water Trucks - Fuel Consumption',
    
    // Refining & Stacking
    'crushed_rock_refining': 'Crushed Rock for Refining',
    'water_sprinkle_stalk_pile': 'Water Sprinkle for Stock Pile',
    'refining_dust': 'Refining Dust Emissions',

    // === A2 TRANSPORT LABELS - COMPLETELY UPDATED ===

    // Intra-Quarry Equipment
    'loader_992_fuel_rate': 'Large Wheel Loader 992 - Fuel Rate',
    'loader_992_hours': 'Large Wheel Loader 992 - Operating Hours',
    'loader_990_fuel_rate': 'Large Wheel Loader 990 - Fuel Rate',
    'loader_990_hours': 'Large Wheel Loader 990 - Operating Hours',
    'loader_aggregate_fuel_rate': 'Wheel Loader (Aggregate Loadout) - Fuel Rate',
    'loader_aggregate_hours': 'Wheel Loader (Aggregate Loadout) - Operating Hours',
    'loader_midsize_fuel_rate': 'Mid-size Wheel Loader - Fuel Rate',
    'loader_midsize_hours': 'Mid-size Wheel Loader - Operating Hours',
    'bulldozer_fuel_rate': 'Bull Dozer - Fuel Rate',
    'bulldozer_hours': 'Bull Dozer - Operating Hours',
    'excavator_mining_fuel_rate': 'Mining Class Excavator - Fuel Rate',
    'excavator_mining_hours': 'Mining Class Excavator - Operating Hours',
    'hydraulic_shovel_fuel_rate': 'Fuel Consumption Rate',
    'hydraulic_shovel_hours': 'Total Operating Hours',
    
    // CAT 988K Loader
    'cat_988k_fuel_rate': 'Fuel Consumption Rate',
    'cat_988k_hours': 'Total Operating Hours',
    // Quarry to Plant Transport
    'haul_truck_offroad_fuel_consumption': 'Fuel Consumption for offroad haul truck (Rock Aggregates)',

    // External Inputs Transport - Bitumen Oil
    'truck_binder_fuel_consumption': 'Fuel Consumption',
    'truck_rap_fuel_consumption': 'Fuel Consumption',
    
    // Rejuvenators Transport
    'truck_rejuvenators_round_trip_distance': 'Distance Travelled',
    'truck_rejuvenators_capacity_divisor': 'Mileage',
    'truck_rejuvenators_trips_per_day': 'Trips Per Day',
    
    // Anti-Strip Transport
    'truck_antistrip_round_trip_distance': 'Distance Travelled',
    'truck_antistrip_capacity_divisor': 'Mileage',
    'truck_antistrip_trips_per_day': 'Trips Per Day',
    
    // Crumb Rubber Transport
    'truck_rubber__fuel_consumption': 'Fuel Consumption',
    // Raw Materials Transport
    'truck_raw_materials_round_trip_distance': 'Distance Travelled',
    'truck_raw_materials_capacity_divisor': 'Mileage',
    'truck_raw_materials_trips_per_day': 'Trips Per Day',
    // Rail Transport of Raw Materials
    'rail_raw_materials_round_trip_distance': 'Distance Travelled',
    'rail_raw_materials_capacity_divisor': 'Mileage',
    'rail_raw_materials_trips_per_day': 'Trips Per Day',
  

    // === A3 MANUFACTURING LABELS - UPDATED ===
    'production_target': 'Production Target',
    // Aggregate Handling
    'diesel_agg_loader': 'Diesel for Aggregate Loader',
    'diesel_rap_loader': 'Diesel for RAP Loader',
    'electricity_virgin_agg_handling': 'Electricity - Virgin Aggregate Handling',
    'electricity_rap_handling': 'Electricity - RAP Handling',
    'electricity_crushing_screening': 'Electricity - Crushing and Screening',
    'dust_emissions_handling': 'Dust Emissions from Handling',
    // Drum Mixing
    'amount_aggregates': 'Amount of Aggregates',
    'amount_oil_mix': 'Amount of Oil Mix',
    'amount_rap': 'Amount of RAP',
    'amount_ras': 'Amount of RAS',
    'amount_rubber': 'Amount of Rubber',
    // Natural Gas (NEW)
    'natural_gas_dryer': 'Natural Gas - Dryer',
    'natural_gas_hot_oil_heater': 'Natural Gas - Hot Oil Heater',
    'natural_gas_rubber_system': 'Natural Gas - Rubber System',
    'natural_gas_drum': 'Natural Gas consumption for Drum/Burner Gas Use',
    'natural_gas_Co_meter': 'Natural Gas consumption for Gas Co. meter',
    // Additives (NEW)
    'amount_pg_64_16': 'Amount of PG 64-16 (Asphalt Binder)',
    'amount_pg_64_10': 'Amount of PG 64-10 (Asphalt Binder)',
    'amount_pg_64_28': 'Amount of PG 64-28 (Asphalt Binder)',
    'amount_ra_5': 'Amount of RA-5 (Recycled Asphalt Binder)',
    'amount_cld_mix': 'Amount of Cold Mix',
    'amount_evoflex_ca6': 'Amount of EvoFlex CA-6 (Modifier)',
    'amount_fiber': 'Amount of Fiber (Reinforcement)',
    'amount_pavegrip': 'Amount of Pavegrip (Additive)',
    'amount_evotherm': 'Amount of Evotherm (Chemical additive)',
    'amount_morlife': 'Amount of Morlife (Modifier)',
    'amount_eco_spa': 'Amount of Eco-Spa (Additive)',
    'amount_latex': 'Amount of Latex (Modifier)',
    'amount_rejuvenator': 'Amount of Rejuvenator',
    'amount_anti_strip': 'Amount of Anti-Strip',
    'fuel_yellow_loader': 'Fuel - Yellow Loader',
    'electricity_drum_mixing': 'Electricity - Drum Mixing System',
    'electricity_binder_additive': 'Electricity - Binder & Additive System',
    'electricity_fiber_rubber': 'Electricity - Fiber & Rubber Additive System',
    // Baghouse Operations
    'electricity_baghouse_dust': 'Electricity - Baghouse & Dust Collection',
    'fuel_deere': 'Fuel - Deere',
    'fuel_skiploader': 'Fuel - Skiploader',
    'residual_dust': 'Residual Dust Emissions',
    // Finished Asphalt Transfer
    'electricity_conveying_elevation': 'Electricity - Conveying & Elevation',
    'electricity_silos_pumps': 'Electricity - Storage Silos & Pumps',
    'fuel_heating_silos': 'Fuel - Heating Silos',
    'fuel_forklift': 'Fuel - Forklift',
    'fuel_manlift': 'Fuel - Manlift',

    // === A4-A5 TRANSPORT FROM PLANT LABELS ===
    // Transport to Site
    // === A4-A5 TRANSPORT FROM PLANT & INSTALLATION LABELS ===
    // A4 - Transport to Site
    'total_diesel_consumption': 'Total Diesel Consumption for Transport to Site',

    // Ancillary Materials Transport
    'quantity_of_tack': 'Quantity of Tack',
    'quantity_of_additives': 'Quantity of Additives',
    'quantity_of_aggregates': 'Quantity of Aggregates',
    'supplier_distance_tack': 'Supplier Distance for Tack',
    'supplier_distance_additives': 'Supplier Distance for Additives',
    'supplier_distance_aggregates': 'Supplier Distance for Aggregates',
    'payload_per_trip': 'Payload per Trip',
    'fuel_efficiency': 'Fuel Efficiency',
    'ancillary_fuel_use': 'Total Fuel Use for Ancillary Materials',

    // A5 - Equipment Mobilization & Installation
    'fuel_lowboy_tractor': 'Fuel - Lowboy Tractor',
    'fuel_ancillary_truck': 'Fuel - Ancillary Truck',
    'fuel_motor_graders': 'Fuel - Motor Graders',
    'fuel_soil_compactors': 'Fuel - Soil Compactors',
    'fuel_skip_loaders': 'Fuel - Skip Loaders',
    'fuel_road_widener': 'Fuel - Road Widener',
    'fuel_paver_6ft': 'Fuel - 6\' Pavers',
    'fuel_paver_8ft': 'Fuel - 8\' Pavers',
    'fuel_paver_10ft': 'Fuel - 10\' Pavers',
    'fuel_asphalt_compactors': 'Fuel - Asphalt Compactors (Rollers)',
    'fuel_pneumatic_rollers': 'Fuel - Pneumatic Rollers',
    'fuel_trench_rollers': 'Fuel - Trench Rollers',
    'fuel_shuttles': 'Fuel - Shuttles (Material Transfer Vehicles)',
    'fuel_loaders': 'Fuel - Loaders',
    'fuel_curb_machines': 'Fuel - Curb Machines',
    'fuel_manhole_division': 'Fuel - Manhole Division',
    'fuel_scrapper': 'Fuel - Scrapper',
    'fuel_sweeper': 'Fuel - Sweeper',
    'fuel_sweeper_kick': 'Fuel - Sweeper Kick',

    // === B2-B5 MAINTENANCE LABELS ===
    'thin_overlay_asphalt': 'Thin Overlay of Asphalt',
    'slurry_seal_emulsion': 'Slurry Seal - Emulsion',
    'slurry_seal_aggregate': 'Slurry Seal - Aggregate',
    'chip_seal_binder': 'Chip Seal - Binder',
    'chip_seal_aggregate': 'Chip Seal - Aggregate',
    'crack_sealant': 'Crack Sealant',
    'fuel_transport_slurry_seal': 'Fuel for Transporting Slurry Seal',

    // === C1-C4 END OF LIFE LABELS ===
    // C1 - Deconstruction
    // C1 - Deconstruction/Demolition
    'fuel_skip_loader': 'Fuel - Skip Loader',
    'fuel_cold_planner': 'Fuel - Cold Planner (Milling Machine)',
    'fuel_backhoe': 'Fuel - BackHoe',
    'fuel_excavator': 'Fuel - Excavator',
    'fuel_root_runner': 'Fuel - Root Runner',
    
    // C2 - Transport
    'fuel_hauling_to_plant': 'Hauling Fuel Consumption to Plant',
    'fuel_loader_diesel': 'Loader Diesel',
    'fuel_stockpile_rehandling': 'Stockpile Rehandling Loader Fuel Consumption',
    
    // C3 - Processing
    'crushing_electricity': 'Crushing Electricity Consumption',
    'screening_electricity': 'Screening Electricity Consumption',
    'loader_diesel_processing': 'Loader Diesel Consumption',
    'conveyers_electricity': 'Conveyers Electricity Consumption',
    'rap_drying_energy': 'RAP Drying Energy',
    
    // C4 - Disposal
    'fraction_to_landfill': 'Fraction to Landfill',
    'landfill_haul_fuel': 'Landfill Haul Fuel',
    'landfill_handling_diesel': 'Landfill Handling Diesel'
  };
  
  return labelMappings[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getFieldUnit = (fieldKey) => {
  // EXTENDED - Enhanced unit mapping for new stages
  const unitMappings = {
    // A1 - Quarry Units (add if missing)
    'renewable_diesel_sandvik_1': 'Gal',
    'renewable_diesel_sandvik_2': 'Gal',
    'drilling_dust': 'Kg',
    
    // Blasting
    'rocks_blasted': 'tons',
    'amount_3_8_inch': 'tons',
    'amount_1_2_inch': 'tons',
    'amount_1_1_2_inch': 'tons',
    'amount_3_4_inch': 'tons',
    'amount_c33': 'tons',
    'subtek_eclipse': 'Kg',
    'blasting_dust': 'Kg',
    
    // Crushing & Conveying
    'rocks_to_crush': 'tons',
    'electricity_primary_crusher_telsmith': 'kWh',
    'electricity_primary_crusher_lippman': 'kWh',
    'electricity_secondary_crusher_telsmith': 'kWh',
    'electricity_tertiary_crusher_telsmith': 'kWh',
    'electricity_quaternary_crusher_telsmith': 'kWh',
    'electricity_feeder_telsmith': 'kWh',
    'electricity_feeder_lippman': 'kWh',
    'crushing_dust': 'kg PM',
    
    // Processing/Screening
    'crushed_rock_processing': 'tons',
    'electricity_scalping_screen_telsmith': 'kWh',
    'electricity_scalping_screen_simplicity': 'kWh',
    'electricity_tertiary_screen': 'kWh',
    'electricity_washscreen': 'kWh',
    'electricity_dewatering_screen': 'kWh',
    'electricity_wash_tank': 'kWh',
    'electricity_fine_material_screw': 'kWh',
    'fuel_water_trucks': 'Gal',
    
    // Refining & Stacking
    'crushed_rock_refining': 'tons',
    'water_sprinkle_stalk_pile': 'Gallons',
    'refining_dust': 'kg PM',
    
    // === A2 TRANSPORT UNITS - COMPLETELY UPDATED ===

    // Intra-Quarry Equipment
    'loader_992_fuel_rate': 'Gal/hr',
    'loader_992_hours': 'hrs/yr',
    'loader_990_fuel_rate': 'Gal/hr',
    'loader_990_hours': 'hrs/yr',
    'loader_aggregate_fuel_rate': 'Gal/hr',
    'loader_aggregate_hours': 'hrs/yr',
    'loader_midsize_fuel_rate': 'Gal/hr',
    'loader_midsize_hours': 'hrs/yr',
    'bulldozer_fuel_rate': 'Gal/hr',
    'bulldozer_hours': 'hrs/yr',
    'excavator_mining_fuel_rate': 'Gal/hr',
    'excavator_mining_hours': 'hrs/yr',
    'hydraulic_shovel_fuel_rate': 'Gal/hr',
    'cat_988k_fuel_rate': 'Gal/hr',
    
    // Equipment - hours
    'hydraulic_shovel_hours': 'hrs/year',
    'cat_988k_hours': 'hrs/year',
    // Quarry to Plant Transport
    'haul_truck_offroad_fuel_consumption': 'Gallons/year',

    // External Inputs Transport
    'truck_binder_fuel_consumption': 'Gallons/year',
    'truck_rap_fuel_consumption': 'Gallons/year',
    // Rejuvenators Transport
    
    'truck_rejuvenators_round_trip_distance': 'Miles',
    'truck_rejuvenators_capacity_divisor': 'Miles/Gal',
    'truck_rejuvenators_trips_per_day': 'trips/day',
    
    'truck_antistrip_round_trip_distance': 'Miles',
    'truck_antistrip_capacity_divisor': 'Miles/Gal',
    'truck_antistrip_trips_per_day': 'trips/day',
    
    'truck_rubber__fuel_consumption': 'Gallons/year',
    // Raw Materials Transport
    'truck_raw_materials_round_trip_distance': 'Miles',
    'truck_raw_materials_capacity_divisor': 'Miles/Gal', 
    'truck_raw_materials_trips_per_day': 'trips/day',
    // Rail Transport of Raw Materials
    'rail_raw_materials_round_trip_distance': 'Miles',
    'rail_raw_materials_capacity_divisor': 'Miles/Gal',
    'rail_raw_materials_trips_per_day': 'trips/day',

    
    // === A3 MANUFACTURING UNITS - UPDATED ===
    'production_target': 'ton HMA',
    // Aggregate Handling
    'diesel_agg_loader': 'Gallons',
    'diesel_rap_loader': 'Gallons',
    'electricity_virgin_agg_handling': 'kWh',
    'electricity_rap_handling': 'kWh',
    'electricity_crushing_screening': 'kWh',
    'dust_emissions_handling': 'kg PM',
    // Drum Mixing
    'amount_aggregates': 'tons',
    'amount_oil_mix': 'tons',
    'amount_rap': 'tons',
    'amount_ras': 'tons',
    'amount_rubber': 'tons',
    // Natural Gas (NEW)
    'natural_gas_dryer': 'GGE',
    'natural_gas_hot_oil_heater': 'GGE',
    'natural_gas_rubber_system': 'GGE',
    'natural_gas_drum': 'GGE',
    'natural_gas_Co_meter': 'GGE',
    // Additives (NEW)
    'amount_pg_64_16': 'tons',
    'amount_pg_64_10': 'tons',
    'amount_pg_64_28': 'tons',
    'amount_ra_5': 'tons',
    'amount_cld_mix': 'tons',
    'amount_evoflex_ca6': 'tons',
    'amount_fiber': 'lbs',
    'amount_pavegrip': 'Gal',
    'amount_evotherm': 'Gal',
    'amount_morlife': 'Gal',
    'amount_eco_spa': 'Gal',
    'amount_latex': 'Gal',
    'amount_rejuvenator': 'tons',
    'amount_anti_strip': 'tons',
    'fuel_yellow_loader': 'Gallons',
    'electricity_drum_mixing': 'kWh',
    'electricity_binder_additive': 'kWh',
    'electricity_fiber_rubber': 'kWh',
    // Baghouse Operations
    'electricity_baghouse_dust': 'kWh',
    'fuel_deere': 'Gallons',
    'fuel_skiploader': 'Gallons',
    'residual_dust': 'kg',
    // Finished Asphalt Transfer
    'electricity_conveying_elevation': 'kWh',
    'electricity_silos_pumps': 'kWh',
    'fuel_heating_silos': 'Gal',
    'fuel_forklift': 'Gallons',
    'fuel_manlift': 'Gallons',
    
    // A4-A5 Units (NEW)
    // A4 - Transport to Site
    'total_diesel_consumption': 'Gallons',

    // Ancillary Materials Transport
    'quantity_of_tack': 'Tons',
    'quantity_of_additives': 'Tons',
    'quantity_of_aggregates': 'Tons',
    'supplier_distance_tack': 'Miles',
    'supplier_distance_additives': 'Miles',
    'supplier_distance_aggregates': 'Miles',
    'payload_per_trip': 'Tons',
    'fuel_efficiency': 'Gal/mile',
    'ancillary_fuel_use': 'Gallons',

    // A5 - Equipment Mobilization & Installation (all in Gallons)
    'fuel_lowboy_tractor': 'Gallons',
    'fuel_ancillary_truck': 'Gallons',
    'fuel_motor_graders': 'Gallons',
    'fuel_soil_compactors': 'Gallons',
    'fuel_skip_loaders': 'Gallons',
    'fuel_road_widener': 'Gallons',
    'fuel_paver_6ft': 'Gallons',
    'fuel_paver_8ft': 'Gallons',
    'fuel_paver_10ft': 'Gallons',
    'fuel_asphalt_compactors': 'Gallons',
    'fuel_pneumatic_rollers': 'Gallons',
    'fuel_trench_rollers': 'Gallons',
    'fuel_shuttles': 'Gallons',
    'fuel_loaders': 'Gallons',
    'fuel_curb_machines': 'Gallons',
    'fuel_manhole_division': 'Gallons',
    'fuel_scrapper': 'Gallons',
    'fuel_sweeper': 'Gallons',
    'fuel_sweeper_kick': 'Gallons',
    
    // B2-B5 Units
    'thin_overlay_asphalt': 'Tons',        // CHANGED from kg
    'slurry_seal_emulsion': 'Tons',        // CHANGED from kg
    'slurry_seal_aggregate': 'Tons',       // CHANGED from kg
    'chip_seal_binder': 'Tons',            // CHANGED from kg
    'chip_seal_aggregate': 'Tons',         // CHANGED from kg
    'crack_sealant': 'Tons',                // CHANGED from kg

    'fuel_transport_slurry_seal': 'Gallons',
    
    // C1-C4 Units
    'fuel_skip_loader': 'Gallons',
    'fuel_cold_planner': 'Gallons',
    'fuel_backhoe': 'Gallons',
    'fuel_excavator': 'Gallons',
    'fuel_root_runner': 'Gallons',

    'fuel_loader_diesel': 'Gallons',
    'fuel_stockpile_rehandling': 'Gallons',
    'fuel_hauling_to_plant': 'Gallons',
    'crushing_electricity': 'kWh',
    'screening_electricity': 'kWh',
    'loader_diesel_processing': 'Gallons',
    'conveyers_electricity': 'kWh',
    'rap_drying_energy': 'MJ',
    'fraction_to_landfill': 'kg waste',
    'landfill_haul_fuel': 'Gallons',
    'landfill_handling_diesel': 'Gallons'
  };
  
  // CHECK MAPPING FIRST before fallback
  if (unitMappings[fieldKey]) {
    return unitMappings[fieldKey];
  }
  
  // Fallback to pattern matching
  if (fieldKey.includes('diesel') || fieldKey.includes('fuel')) return 'Gallons';
  if (fieldKey.includes('electricity') || fieldKey.includes('power')) return 'kWh';
  if (fieldKey.includes('water')) return 'Gallons';
  if (fieldKey.includes('distance')) return 'km';
  if (fieldKey.includes('mileage')) return 'km/L';
  if (fieldKey.includes('efficiency') && !fieldKey.includes('fuel')) return 'km/kWh';
  if (fieldKey.includes('payload')) return 'kg/trip';
  if (fieldKey.includes('mass') || fieldKey.includes('weight')) return 'kg';
  if (fieldKey.includes('target')) return 'kg HMA';
  
  return ''; // No unit found
};

const isPartialNumericInput = (value) => {
  if (typeof value !== 'string') return false;
  return (
    value === '' ||
    value === '-' ||
    value === '.' ||
    value === '-.' ||
    /^-?\d+\.$/.test(value)
  );
};

const toNumericValue = (value, { treatEmptyAsZero = true } = {}) => {
  if (typeof value === 'number') return value;
  if (value === '' || value === null || value === undefined) {
    return treatEmptyAsZero ? 0 : '';
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return treatEmptyAsZero ? 0 : '';
  }
  return parsed;
};

const blurNumberInputOnWheel = (event) => {
  if (event?.preventDefault) {
    event.preventDefault();
  }

  if (event?.stopPropagation) {
    event.stopPropagation();
  }

  if (event?.currentTarget) {
    event.currentTarget.blur();
  }
};


const sanitizeStageValues = (stageData = {}) => {
  const sanitized = {};
  Object.entries(stageData || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) {
      sanitized[key] = 0;
      return;
    }
    if (typeof value === 'number') {
      sanitized[key] = value;
      return;
    }
    const parsed = Number(value);
    sanitized[key] = Number.isNaN(parsed) ? 0 : parsed;
  });
  return sanitized;
};

const NUMERIC_STAGE_KEYS = [
  'quarry',
  'transport',
  'asphalt_manufacturing',
  'transport_from_plant',
  'maintenance',
  'end_of_life'
];

const buildSanitizedScenarioPayload = (scenarioData) => {
  const base = { ...scenarioData };
  NUMERIC_STAGE_KEYS.forEach((stageKey) => {
    base[stageKey] = sanitizeStageValues(scenarioData?.[stageKey]);
  });
  return base;
};

const CreateScenarioWizard = () => {
  const wizardRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityDefaults, setSelectedFacilityDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingBasic, setSavingBasic] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!searchParams.get('edit');

  // EXTENDED SCENARIO DATA - Now includes all 6 stages (MOVED UP)
  const [scenarioData, setScenarioData] = useState({
    name: '',
    facility: '',
    comments: '',
    quarry: {},
    transport: {},
    asphalt_manufacturing: {},
    // NEW STAGES ADDED
    transport_from_plant: {},
    maintenance: {},
    end_of_life: {}
  });

  // EXTENDED DYNAMIC STEPS - Now includes all 6 stages (MOVED UP)
  const [dynamicSteps, setDynamicSteps] = useState([
    { 
      id: 'basic', 
      title: 'Basic Information', 
      icon: '📝',
      description: 'Enter scenario details'
    }
  ]);

  // === VALIDATION STATE === (MOVED DOWN, AFTER dynamicSteps)
  const [validationErrors, setValidationErrors] = useState({});
  
  // Get current stage name for validation
  const getCurrentStageName = () => {
    const step = dynamicSteps[currentStep];
    if (!step || step.id === 'basic' || step.id === 'review') return null;
    return step.id;
  };
  
  // Initialize validation hook
  // Initialize validation hook
  const currentStageName = getCurrentStageName();
  const {
    validationState,
    handleFieldChange: validateField,
    getFieldError,
    hasFieldError,
    canProceed: canProceedValidation,
    handleAutoFixAggregates,
    forceValidate
  } = useScenarioValidation(
    scenarioData,
    currentStageName || 'quarry',
    true // validate on change
  );

  // ADD THIS DEBUG
  useEffect(() => {
    console.log('📊 Validation Update:', {
      currentStageName,
      validationState,
      canProceed: canProceedValidation,
      scenarioData: currentStageName ? scenarioData[currentStageName] : null
    });
  }, [validationState, canProceedValidation, currentStageName, scenarioData]);

  useEffect(() => {
    loadFacilities();
    
    // Check if editing existing scenario
    const editId = searchParams.get('edit');
    if (editId) {
      loadExistingScenario(editId);
    }
  }, [searchParams]);

  useEffect(() => {
    // Load facility defaults when facility is selected
    if (scenarioData.facility) {
      loadFacilityDefaults(scenarioData.facility);
    }
  }, [scenarioData.facility]);

  useEffect(() => {
    // EXTENDED STEPS - Now includes all 6 stages
    if (selectedFacilityDefaults?.activities) {
      const steps = [
        { 
          id: 'basic', 
          title: 'Basic Information', 
          icon: '📝',
          description: 'Enter scenario details'
        }
      ];

      // Add dynamic steps based on facility activities - EXTENDED
      const stageConfig = {
        quarry: { title: 'Quarry Stage', icon: '⛏️', description: 'Raw material extraction' },
        transport: { title: 'Transport Stage', icon: '🚛', description: 'Transportation logistics' },
        asphalt_manufacturing: { title: 'Manufacturing', icon: '🏭', description: 'Asphalt production' },
        // NEW STAGES ADDED
        transport_from_plant: { title: 'Transport from Plant', icon: '🚚', description: 'Asphalt delivery & Construction' },
        maintenance: { title: 'Maintenance', icon: '🔧', description: 'Pavement maintenance' },
        end_of_life: { title: 'End-of-Life', icon: '♻️', description: '' }
      };

      Object.keys(selectedFacilityDefaults.activities).forEach(stageKey => {
        const config = stageConfig[stageKey];
        if (config) {
          steps.push({
            id: stageKey,
            title: config.title,
            icon: config.icon,
            description: config.description
          });
        }
      });

      steps.push({ 
        id: 'review', 
        title: 'Review & Calculate', 
        description: 'Final review'
      });

      setDynamicSteps(steps);
    }
  }, [selectedFacilityDefaults]);

  const loadFacilities = async () => {
    try {
      const response = await apiService.getFacilities();
      setFacilities(response.data);
    } catch (err) {
      console.error('Failed to load facilities:', err);
      setError('Failed to load facilities');
    }
  };

  const loadFacilityDefaults = async (facilityName) => {
    try {
      setLoading(true);
      const response = await apiService.getFacilityDefaults(facilityName);
      setSelectedFacilityDefaults(response.data);
      
      // EXTENDED - Merge facility defaults with current scenario data for ALL stages
      const defaults = response.data.default_values;
      setScenarioData(prev => {
        // If in edit mode and scenario data already loaded, preserve it
        const shouldPreserveData = isEditMode && Object.keys(prev.quarry || {}).length > 0;
        
        if (shouldPreserveData) {
          // Edit mode: Keep existing scenario data
          return prev;
        } else {
          // Create mode or facility switch: Replace with new defaults
          return {
            ...prev,
            quarry: { ...defaults.quarry },
            transport: { ...defaults.transport },
            asphalt_manufacturing: { ...defaults.asphalt_manufacturing },
            transport_from_plant: { ...defaults.transport_from_plant },
            maintenance: { ...defaults.maintenance },
            end_of_life: { ...defaults.end_of_life }
          };
        }
      });
    } catch (err) {
      console.error('Failed to load facility defaults:', err);
      setError('Failed to load facility configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingScenario = async (scenarioId) => {
    try {
      setLoading(true);
      const response = await apiService.getScenario(scenarioId);
      const scenario = response.data;
      
      // EXTENDED - Load all stage data including new stages
      setScenarioData({
        name: scenario.name,
        facility: scenario.facility,
        comments: scenario.comments || '',
        quarry: scenario.quarry || {},
        transport: scenario.transport || {},
        asphalt_manufacturing: scenario.asphalt_manufacturing || {},
        // NEW STAGES FROM EXISTING SCENARIO
        transport_from_plant: scenario.transport_from_plant || {},
        maintenance: scenario.maintenance || {},
        end_of_life: scenario.end_of_life || {}
      });
    } catch (err) {
      console.error('Failed to load scenario:', err);
      setError('Failed to load scenario for editing');
    } finally {
      setLoading(false);
    }
  };

  const updateScenarioData = (key, value) => {
    setScenarioData(prev => ({ ...prev, [key]: value }));
  };

  const updateStageData = (stage, data) => {
    setScenarioData(prev => ({
      ...prev,
      [stage]: { ...prev[stage], ...data }
    }));
  };

  const scrollToWizardTop = () => {
    const scrollContainer = wizardRef.current?.closest('.content');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const nextStep = () => {
    if (currentStep < dynamicSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
      scrollToWizardTop();
    }
  };

  const handleSaveBasicInfo = async () => {
    const editId = searchParams.get('edit');
    if (!editId || !scenarioData.name || !scenarioData.facility) {
      return;
    }

    try {
      setSavingBasic(true);
      setError(null);
      const payload = buildSanitizedScenarioPayload(scenarioData);
      await apiService.updateScenario(editId, payload);
    } catch (err) {
      console.error('Failed to save scenario:', err);
      setError(err.response?.data?.detail || 'Failed to save scenario');
    } finally {
      setSavingBasic(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      scrollToWizardTop();
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = buildSanitizedScenarioPayload(scenarioData);
      // Add user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payloadWithTimezone = { ...payload, timezone };
      const editId = searchParams.get('edit');
      let response;
      
      if (editId) {
        response = await apiService.updateScenario(editId, payloadWithTimezone);
      } else {
        response = await apiService.createScenario(payloadWithTimezone);
      }
      
      const savedScenario = response.data;
      navigate(`/dashboard?scenario=${savedScenario._id}`);
      
    } catch (err) {
      console.error('Failed to save scenario:', err);
      setError(err.response?.data?.detail || 'Failed to save scenario');
      setLoading(false);
    }
  };

  const canProceed = () => {
    const step = dynamicSteps[currentStep];
    
    // Basic info step
    if (step?.id === 'basic') {
      return scenarioData.name && scenarioData.facility;
    }
    
    // Review step
    if (step?.id === 'review') {
      return true;
    }
    
    // Stage steps - check validation
    if (currentStageName) {
      return canProceedValidation;
    }
    
    return true;
  };

  const renderStepContent = () => {
    const step = dynamicSteps[currentStep];
    if (!step) return null;

    switch (step.id) {
      case 'basic':
        return <BasicInfoStep 
          data={scenarioData} 
          facilities={facilities} 
          onChange={updateScenarioData} 
        />;
      case 'review':
        return <ReviewStep 
          data={scenarioData} 
          facilityDefaults={selectedFacilityDefaults}
          steps={dynamicSteps}
          onSubmit={handleSubmit} 
          loading={loading}
          isEdit={!!searchParams.get('edit')}
          onDataChange={updateStageData}
        />;
      default:
        // EXTENDED - Dynamic stage step for ALL 6 stages
        if (selectedFacilityDefaults?.activities?.[step.id]) {
          return <DynamicStageStep
            stageKey={step.id}
            stageTitle={step.title}
            stageIcon={step.icon}
            stageDescription={step.description}
            activities={selectedFacilityDefaults.activities[step.id]}
            data={scenarioData[step.id] || {}}
            defaults={selectedFacilityDefaults.default_values?.[step.id] || {}}
            onChange={(data) => updateStageData(step.id, data)}
            // ADD: Pass validation props
            validationState={validationState}
            handleAutoFixAggregates={handleAutoFixAggregates}
            hasFieldError={hasFieldError}
            getFieldError={getFieldError}
          />;
        }
        return null;
    }
  };

  if (loading && facilities.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading wizard...</p>
      </div>
    );
  }

  return (
    <div className="create-scenario-wizard" ref={wizardRef}>
      {/* Header */}


      {/* EXTENDED Progress Bar - Now shows up to 9 steps total */}
      <div className="wizard-progress">
        <div className="progress-container">
          {dynamicSteps.map((step, index) => (
            <div
              key={step.id}
              className={`progress-step ${index <= currentStep ? 'active' : ''} ${index === currentStep ? 'current' : ''}`}
            >
              <div className="step-connector" />
              <div className="step-circle">
                <span className="step-icon">{step.icon}</span>
              </div>
              <div className="step-info">
                <div className="step-title">{step.title}</div>
                <div className="step-description">{step.description}</div>
              </div>
            </div>
          ))}
        </div>
        <div 
          className="progress-bar-fill" 
          style={{ width: `${((currentStep + 1) / dynamicSteps.length) * 100}%` }}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Loading overlay for facility defaults */}
      {loading && selectedFacilityDefaults === null && scenarioData.facility && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading facility configuration...</p>
        </div>
      )}

      {/* Step Content */}
      <div className="wizard-content">
        {renderStepContent()}
      </div>

      {/* CORRECTED Navigation */}
      <div className="wizard-navigation">
        <div className="navigation-inner">
          <div className="nav-left">
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className="btn btn-outline"
                disabled={loading}
              >
                {/* <span className="btn-icon">←</span> */}
                Previous
              </button>
            )}
          </div>

          <div className="nav-center">
            <div className="step-indicator">
              {currentStep + 1} / {dynamicSteps.length}
            </div>
          </div>

          <div className="nav-right">
            {currentStep === 0 && isEditMode ? (
              <>
                <button
                  onClick={handleSaveBasicInfo}
                  className="btn btn-outline"
                  disabled={!canProceed() || loading || savingBasic}
                >
                  {savingBasic ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={nextStep}
                  className="btn btn-primary"
                  disabled={!canProceed() || loading}
                >
                  Next
                  {/* <span className="btn-icon">→</span> */}
                </button>
              </>
            ) : currentStep < dynamicSteps.length - 1 ? (
              <button
                onClick={nextStep}
                className="btn btn-primary"
                disabled={!canProceed() || loading}
                title={!canProceed() ? 'Please fix validation errors before proceeding' : ''}
              >
                Next
                {/* <span className="btn-icon">→</span> */}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="btn btn-success btn-large"
                disabled={loading || !canProceed()}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner small"></div>
                    {searchParams.get('edit') ? 'Updating...' : 'Calculating...'}
                  </>
                ) : (
                  <>
                    {/* <span className="btn-icon">🧮</span> */}
                    {searchParams.get('edit') ? 'Update & Recalculate' : 'Calculate & Save'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
};

// Basic Info Step Component - HEADER REMOVED
const BasicInfoStep = ({ data, facilities, onChange }) => (
  <div className="step-content">
    <div className="form-container">
      <div className="form-group">
        <label className="form-label required">Scenario Name</label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="e.g., Highway 101 Resurfacing Project"
          className="form-input large"
        />
        <span className="form-hint">Choose a descriptive name for easy identification</span>
      </div>

      <div className="form-group">
        <label className="form-label required">Facility</label>
        <select
          value={data.facility}
          onChange={(e) => onChange('facility', e.target.value)}
          className="form-select large"
        >
          <option value="">Select a facility...</option>
          {facilities.map(facility => (
            <option key={facility.id || facility.name} value={facility.name}>
              {facility.name}
            </option>
          ))}
        </select>
        <span className="form-hint">Select the facility where this asphalt will be produced</span>
      </div>

      <div className="form-group">
        <label className="form-label">Comments</label>
        <textarea
          value={data.comments}
          onChange={(e) => onChange('comments', e.target.value)}
          placeholder="Any additional notes about this scenario..."
          className="form-textarea"
          rows="4"
        />
        <span className="form-hint">Optional: Add any relevant notes or specifications</span>
      </div>
    </div>
  </div>
);

// PRESERVED - Dynamic Stage Step Component (handles all stages including new ones)
const DynamicStageStep = ({ 
  stageKey, 
  stageTitle, 
  stageIcon, 
  stageDescription, 
  activities, 
  data, 
  defaults, 
  onChange,
  validationState,
  handleAutoFixAggregates,
  hasFieldError,
  getFieldError 
}) => {
  const handleFieldChange = (fieldKey, value) => {
    if (isPartialNumericInput(value)) {
      onChange({ [fieldKey]: value });
      return;
    }

    const numericValue = toNumericValue(value, { treatEmptyAsZero: false });
    if (numericValue === '') {
      onChange({ [fieldKey]: '' });
      return;
    }

    // === VALIDATION: Prevent negative values ===
    if (numericValue < 0) {
      return;
    }

    // Update the data
    onChange({ [fieldKey]: numericValue });
  };

  const groupFields = (fields) => {
    const groups = {};
    
    fields.forEach(field => {
      let category = 'General';
      
      // STAGE-BASED GROUPING - Check stage context first to avoid misclassification
      
      // === A1 QUARRY STAGE GROUPING ===
      if (stageKey === 'quarry') {
        // Drilling Activities (Renewable Diesel Equipment)
        if (['renewable_diesel_sandvik_1', 'renewable_diesel_sandvik_2', 'drilling_dust'].includes(field)) {
          category = '⛏️ Drilling Activities';
        }
        // Blasting Activities  
        else if (['rocks_blasted', 'amount_3_8_inch', 'amount_1_2_inch', 
            'amount_1_1_2_inch', 'amount_3_4_inch', 'amount_c33', 'subtek_eclipse', 'blasting_dust'].includes(field)) {
          category = '💥 Blasting Activities';
        }
        // Crushing and Conveying (6 crushers/feeders)
        else if ([
          'rocks_to_crush', 
          'electricity_primary_crusher_telsmith', 
          'electricity_primary_crusher_lippman',
          'electricity_secondary_crusher_telsmith',
          'electricity_tertiary_crusher_telsmith',
          'electricity_quaternary_crusher_telsmith',
          'electricity_feeder_telsmith',
          'electricity_feeder_lippman',
          'crushing_dust'
        ].includes(field)) {
          category = '🔨 Crushing & Conveying';
        }
        // Processing/Screening (7 operations + water trucks)
        else if ([
          'crushed_rock_processing',
          'electricity_scalping_screen_telsmith',
          'electricity_scalping_screen_simplicity',
          'electricity_tertiary_screen',
          'electricity_washscreen',
          'electricity_dewatering_screen',
          'electricity_wash_tank',
          'electricity_fine_material_screw',
          'fuel_water_trucks'
        ].includes(field)) {
          category = '⚙️ Processing & Screening';
        }
        // Refining and Stacking
        else if ([
          'crushed_rock_refining', 
          'water_sprinkle_stalk_pile', 
          'refining_dust'
        ].includes(field)) {
          category = '📦 Refining & Stacking';
        }
        // Raw Material Inventory (NEW)
        else if (['amount_pg_64_16', 'amount_pg_64_10', 'amount_pg_64_28', 'amount_ra_5', 'amount_cld_mix', 'amount_rubber', 'amount_evoflex_ca6', 'amount_fiber', 'amount_pavegrip', 'amount_evotherm', 'amount_morlife', 'amount_eco_spa', 'amount_latex','amount_rejuvenator','amount_anti_strip'].includes(field)) {
          category = '🧪 Raw Material - Inventory';
        }
      }
      
      // === A2 TRANSPORT STAGE GROUPING ===
      else if (stageKey === 'transport') {
        // Intra-Quarry Equipment
        if (['loader_992_fuel_rate', 'loader_992_hours'].includes(field)) {
          category = '🚜 Large Wheel Loader 992';
        }
        else if (['loader_990_fuel_rate', 'loader_990_hours'].includes(field)) {
          category = '🚜 Large Wheel Loader 990';
        }
        else if (['loader_aggregate_fuel_rate', 'loader_aggregate_hours'].includes(field)) {
          category = '🚜 Wheel Loader - Aggregate Loadout';
        }
        else if (['loader_midsize_fuel_rate', 'loader_midsize_hours'].includes(field)) {
          category = '🚜 Mid-size Wheel Loader';
        }
        else if (['bulldozer_fuel_rate', 'bulldozer_hours'].includes(field)) {
          category = '🏗️ Bull Dozer';
        }
        else if (['excavator_mining_fuel_rate', 'excavator_mining_hours'].includes(field)) {
          category = '⛏️ Mining Class Excavator';
        }
        // Quarry to Plant Transport
        else if (['haul_truck_offroad_fuel_consumption'].includes(field)) {
          category = '🚚 Off-road Haul Truck';
        }
        else if (['hydraulic_shovel_fuel_rate', 'hydraulic_shovel_hours'].includes(field)) {
          category = '🚜 Hydraulic Shovel 2';
        }

        // NEW: CAT 988K Loader  
        else if (['cat_988k_fuel_rate', 'cat_988k_hours'].includes(field)) {
          category = '🚜 CAT 988K Loader';
        }
        // External Inputs Transport
        else if (['truck_binder_fuel_consumption'].includes(field)) {
          category = '🛻 Heavy Duty Truck - Virgin Binder';
        }
        else if (['truck_rap_fuel_consumption'].includes(field)) {
          category = '🛻 Heavy Duty Truck - RAP';
        }
        else if (['truck_rejuvenators_round_trip_distance', 'truck_rejuvenators_capacity_divisor', 'truck_rejuvenators_trips_per_day'].includes(field)) {
          category = '🛻 Heavy Duty Truck - Rejuvenators';
        }
        else if (['truck_antistrip_round_trip_distance', 'truck_antistrip_capacity_divisor', 'truck_antistrip_trips_per_day'].includes(field)) {
          category = '🛻 Heavy Duty Truck - Anti-Strip';
        }
        else if (['truck_rubber__fuel_consumption'].includes(field)) {
          category = '🛻 Heavy Duty Truck - Rubber';
        }
        else if (['truck_raw_materials_round_trip_distance', 'truck_raw_materials_capacity_divisor', 'truck_raw_materials_trips_per_day'].includes(field)) {
          category = '🛻 Raw Materials Trucking';
        }
        else if (['rail_raw_materials_round_trip_distance', 'rail_raw_materials_capacity_divisor', 'rail_raw_materials_trips_per_day'].includes(field)) {
          category = '🚂 Raw Materials Rail Transport';
        }

        else {
          category = '🚛 General Transport';
        }
      }
      
      // === A3 MANUFACTURING STAGE GROUPING ===
      else if (stageKey === 'asphalt_manufacturing') {
        if (field === 'production_target') {
          category = '🎯 Production Target';
        }
        // Aggregate Handling
        else if (['diesel_agg_loader', 'diesel_rap_loader'].includes(field)) {
          category = '🚜 Aggregate Handling - Diesel';
        }
        else if (['electricity_virgin_agg_handling', 'electricity_rap_handling', 'electricity_crushing_screening'].includes(field)) {
          category = '⚡ Aggregate Handling - Electricity';
        }
        else if (field === 'dust_emissions_handling') {
          category = '💨 Aggregate Handling - Emissions';
        }
        // Natural Gas (NEW)
        else if (['natural_gas_dryer', 'natural_gas_hot_oil_heater', 'natural_gas_rubber_system','natural_gas_drum','natural_gas_Co_meter'].includes(field)) {
          category = '🔥 Natural Gas Consumption';
        }
        // Drum Mixing - Materials
        else if (['amount_aggregates', 'amount_oil_mix', 'amount_rap', 'amount_ras', 'amount_rubber'].includes(field)) {
          category = '🪨 Drum Mixing - Base Materials';
        }
        
        // Drum Mixing - Fuel
        else if (['fuel_red_loader','fuel_white_loader','fuel_blue_loader', 'fuel_yellow_loader'].includes(field)) {
          category = '⛽ Drum Mixing - Fuel';
        }
        // Drum Mixing - Electricity
        else if (['electricity_drum_mixing', 'electricity_binder_additive', 'electricity_fiber_rubber'].includes(field)) {
          category = '⚡ Drum Mixing - Electricity';
        }
        // Baghouse Operations - Electricity
        else if (field === 'electricity_baghouse_dust') {
          category = '⚡ Baghouse - Electricity';
        }
        // Baghouse Operations - Fuel
        else if (['fuel_deere', 'fuel_skiploader'].includes(field)) {
          category = '⛽ Baghouse - Fuel';
        }
        // Baghouse Operations - Emissions
        else if (field === 'residual_dust') {
          category = '💨 Baghouse - Emissions';
        }
        // Finished Asphalt Transfer - Electricity
        else if (['electricity_conveying_elevation', 'electricity_silos_pumps'].includes(field)) {
          category = '⚡ Asphalt Transfer - Electricity';
        }
        // Finished Asphalt Transfer - Fuel
        else if (['fuel_heating_silos', 'fuel_forklift', 'fuel_manlift'].includes(field)) {
          category = '⛽ Asphalt Transfer - Fuel';
        }
        else {
          category = '🏭 General Manufacturing';
        }
      }
      
      // === A4-A5 TRANSPORT FROM PLANT STAGE GROUPING ===
      else if (stageKey === 'transport_from_plant') {
        // Transport to Site
        if (['distance_from_plant', 'tons_delivered', 'payload_per_trip', 'trips', 'fuel_efficiency', 'total_fuel_use'].includes(field)) {
          category = '🚛 Transport to Site';
        }
        // Ancillary Materials Transport
        else if (['quantity_of_tack', 'quantity_of_additives', 'quantity_of_aggregates', 'supplier_distance_tack', 'supplier_distance_additives', 'supplier_distance_aggregates', 'ancillary_payload_per_trip', 'ancillary_total_trips', 'ancillary_fuel_efficiency', 'ancillary_fuel_use'].includes(field)) {
          category = '📦 Ancillary Materials Transport';
        }
        // Equipment Mobilization
        else if (['lowboy_tractor_diesel', 'flatbed_rollers_distance', 'paver_hauling_distance', 'mtv_hauling_distance', 'mobilization_trips', 'mobilization_fuel_efficiency'].includes(field)) {
          category = '🚚 Equipment Mobilization';
        }
        // Surface Preparation
        else if (['sweeper_hours', 'sweeper_fuel_efficiency', 'sweeper_fuel_consumption', 'sprayer_hours', 'sprayer_fuel_efficiency', 'sprayer_fuel_consumption', 'saw_cutting_water'].includes(field)) {
          category = '🧹 Surface Preparation';
        }
        // Asphalt Laydown
        else if (['paver_operation_hours', 'paver_fuel_efficiency', 'paver_diesel_consumption', 'mtv_operation_hours', 'mtv_fuel_efficiency', 'mtv_diesel_consumption', 'asphalt_quantity_placed', 'temperature_mix', 'waste_asphalt_mix'].includes(field)) {
          category = '🏗️ Asphalt Laydown';
        }
        // Compaction
        else if (['roller_fleet_hours', 'roller_fleet_fuel_efficiency', 'roller_fuel_consumption'].includes(field)) {
          category = '🔄 Compaction';
        }
        // Water Use on Site
        else if (['water_truck_hours', 'water_truck_fuel_efficiency', 'water_truck_diesel_consumption', 'water_use_onsite'].includes(field)) {
          category = '💧 Water Use on Site';
        }
        // Construction Waste Handling
        else if (['unused_mix_returned', 'packaging_waste_transport'].includes(field)) {
          category = '🗑️ Construction Waste Handling';
        }
        else {
          category = '🚚 General Installation';
        }
      }
      
      // === B2-B5 MAINTENANCE GROUPING ===
      else if (stageKey === 'maintenance') {
        // Activity (Materials)
        if (['thin_overlay_asphalt', 'slurry_seal_emulsion', 'slurry_seal_aggregate', 'chip_seal_binder', 'chip_seal_aggregate', 'crack_sealant'].includes(field)) {
          category = '🔧 Maintenance Activities';
        }
        // Equipment Fuel Consumption
        else if (['asphalt_paver_fuel', 'rollers_fuel', 'sweeper_fuel', 'track_sprayer_fuel', 'water_truck_fuel'].includes(field)) {
          category = '🚜 Equipment Fuel Consumption';
        }
        // Water Use
        else if (['compaction_water'].includes(field)) {
          category = '💧 Water Use';
        }
        else {
          category = '🔧 General Maintenance';
        }
      }
      
      // === C1-C4 END OF LIFE GROUPING ===
      else if (stageKey === 'end_of_life') {
        // C1 - Deconstruction (Milling and Removal)
        if (['milling_diesel', 'dust_suppression_water', 'milling_depth', 'milled_area', 'asphalt_density'].includes(field)) {
          category = '🔨 C1 - Deconstruction (Milling and Removal)';
        }
        // C2 - Transport of Milled Material
        else if (['hauling_fuel_to_plant', 'loader_diesel_transport', 'stockpile_rehandling_fuel'].includes(field)) {
          category = '🚛 C2 - Transport of Milled Material';
        }
        // C3 - Processing of RAP
        else if (['crushing_electricity', 'screening_electricity', 'loader_diesel_processing', 'conveyers_electricity', 'rap_drying_energy'].includes(field)) {
          category = '♻️ C3 - Processing of RAP';
        }
        // C4 - Disposal (If RAP is not reused)
        else if (['fraction_to_landfill', 'landfill_haul_fuel', 'landfill_handling_diesel'].includes(field)) {
          category = '🗑️ C4 - Disposal (If RAP is not reused)';
        }
        else {
          category = '♻️ General End-of-Life';
        }
      }
      
      // === FALLBACK FOR UNKNOWN STAGES ===
      else {
        category = 'General';
      }
      
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(field);
    });
    
    return groups;
  };

  const userInputActivities = activities.filter(activity => {
    if (typeof activity === 'string') return true;
    return activity.visibility_type === 'user_input';
  });
  
  const userInputFieldNames = userInputActivities.map(activity => 
    typeof activity === 'string' ? activity : activity.field
  );
  
  const groupedFields = groupFields(userInputFieldNames);

  if (activities.length === 0) {
    return (
      <div className="no-activities">
        <p>No activities configured for this stage in the selected facility.</p>
      </div>
    );
  }

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>{stageIcon} {stageTitle}</h2>
        <p>{stageDescription}</p>
      </div>
      {/* === VALIDATION ALERT === */}
      <ValidationAlert 
        validationResult={validationState}
        stageName={stageTitle}
        onAutoFix={stageKey === 'quarry' ? handleAutoFixAggregates : null}
        showDetails={true}
      />
      {/* ADD THIS: Direct aggregate mismatch alert for Quarry */}
      {stageKey === 'quarry' && (() => {
        const rocksBlasted = data.rocks_blasted || 0;
        const aggregateSum = 
          (data.amount_3_8_inch || 0) +
          (data.amount_1_2_inch || 0) +
          (data.amount_1_1_2_inch || 0) +
          (data.amount_3_4_inch || 0) +
          (data.amount_c33 || 0);
        const difference = Math.abs(rocksBlasted - aggregateSum);
        const hasMismatch = difference > 0.0001;
        
        if (!hasMismatch) return null;
        
        return (
          <div className="aggregate-mismatch-alert">
            <div className="alert-header">
              <span className="alert-icon">⚠️</span>
              <h3>Aggregate Balance Error</h3>
            </div>
            <div className="alert-content">
              <p className="alert-message">
                <strong>Rocks Blasted:</strong> {rocksBlasted.toFixed(6)} tons
                <br />
                <strong>Sum of Aggregates:</strong> {aggregateSum.toFixed(6)} tons
                <br />
                <strong>Difference:</strong> {difference.toFixed(6)} tons
              </p>
              <p className="alert-explanation">
                The amount of rocks blasted must equal the sum of all aggregate sizes 
                (3/8" + 1/2" + 1-1/2" + 3/4" + C33).
              </p>
              {handleAutoFixAggregates && (
                <button 
                  type="button"
                  onClick={() => {
                    const fixed = handleAutoFixAggregates();
                    if (fixed) {
                      onChange(fixed);
                    }
                  }}
                  className="btn-auto-fix-inline"
                >
                  🔧 Auto-Fix: Set Rocks Blasted = {aggregateSum.toFixed(6)}
                </button>
              )}
            </div>
          </div>
        );
      })()}
      <div className="dynamic-form">
        {Object.entries(groupedFields).map(([category, fields]) => (
          <div key={category} className="field-group">
            <h3 className="group-title">{category}</h3>
            
            <div className="fields-grid">
              {fields.map(fieldKey => (
                <div key={fieldKey} className="form-group">
                  <label className="form-label">
                    {getFieldLabel(fieldKey)}
                    {getFieldUnit(fieldKey) && (
                      <span className="unit"> ({getFieldUnit(fieldKey)})</span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"  // ADD: Prevent negative values
                    value={data[fieldKey] ?? ''}
                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                    onWheel={blurNumberInputOnWheel}
                    className={`form-input ${hasFieldError(fieldKey) ? 'input-error' : ''}`}  // ADD: Error class
                    placeholder="Enter value"
                    required
                    aria-invalid={hasFieldError(fieldKey)}  // ADD: Accessibility
                  />
                  {/* ADD: Error message display */}
                  {hasFieldError(fieldKey) && (
                    <FieldValidationError 
                      error={getFieldError(fieldKey)}
                      show={true}
                    />
                  )}

                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// REVIEW STEP COMPONENT
const ReviewStep = ({ 
  data = {},
  facilityDefaults,
  steps = [],
  onSubmit,
  loading,
  isEdit,
  onDataChange
}) => {
  const [editingStage, setEditingStage] = useState(null);
  const [localValues, setLocalValues] = useState({});

  // Filter out basic and review steps to get only stage steps
  const stageSteps = steps.filter(step => 
    step.id !== 'basic' && step.id !== 'review'
  );

  // Get ONLY parameters for a stage (not user inputs, not hidden)
  const getStageParameters = (stageId) => {
    const activities = facilityDefaults?.activities?.[stageId] || [];
    const parameters = [];
    
    activities.forEach(activity => {
      // Handle both old format (string) and new format (object)
      if (typeof activity === 'string') {
        // Old format - skip
        return;
      }
      
      // New format - check visibility_type
      if (activity.visibility_type === 'parameter') {
        parameters.push(activity.field);
      }
    });
    
    return parameters;
  };

  // Handle parameter value change
  const handleParameterChange = (stageId, fieldKey, value) => {
    const numValue = parseFloat(value) || 0;
    
    // Update local state
    setLocalValues(prev => ({
      ...prev,
      [stageId]: {
        ...(prev[stageId] || {}),
        [fieldKey]: numValue
      }
    }));
    
    // Update parent state
    if (onDataChange) {
      onDataChange(stageId, { [fieldKey]: numValue });
    }
  };

  // Get current value for a field (local edit > scenario data > defaults)
  const getCurrentValue = (stageId, fieldKey) => {
    if (localValues[stageId]?.[fieldKey] !== undefined) {
      return localValues[stageId][fieldKey];
    }
    if (data[stageId]?.[fieldKey] !== undefined) {
      return data[stageId][fieldKey];
    }
    return facilityDefaults?.default_values?.[stageId]?.[fieldKey] || 0;
  };

  // Check if value is modified from default
  const isModified = (stageId, fieldKey) => {
    const currentValue = getCurrentValue(stageId, fieldKey);
    const defaultValue = facilityDefaults?.default_values?.[stageId]?.[fieldKey];
    return currentValue !== defaultValue;
  };

  // Render stage parameters
  const renderStageParameters = (step) => {
    const stageId = step.id;
    const parameters = getStageParameters(stageId);
    const isEditing = editingStage === stageId;

    if (parameters.length === 0) {
      return null;
    }

    return (
      <div key={stageId} className="stage-review-card">
        <div className="stage-review-header">
          <h3>
            <span className="stage-icon">{step.icon}</span>
            {step.title}
          </h3>
          <button
            className={`btn-edit ${isEditing ? 'editing' : ''}`}
            onClick={() => setEditingStage(isEditing ? null : stageId)}
            type="button"
          >
            {isEditing ? '✓ Done' : '✏️ Edit Parameters'}
          </button>
        </div>

        <div className="parameters-section">
          <div className="parameters-header">
            <h4>⚙️ Parameters (per ton of asphalt)</h4>
            {!isEditing && (
              <span className="badge-default">Default Values</span>
            )}
          </div>

          <div className={`parameters-grid ${isEditing ? 'editing-mode' : ''}`}>
            {parameters.map(fieldKey => {
              const currentValue = getCurrentValue(stageId, fieldKey);
              const modified = isModified(stageId, fieldKey);
              const unit = getFieldUnit(fieldKey);
              
              return (
                <div key={fieldKey} className="parameter-field">
                  <label className="parameter-label">
                    <span className="label-text">
                      {getFieldLabel(fieldKey)}
                      {unit && <span className="field-unit"> ({unit})</span>}
                    </span>
                    {modified && <span className="modified-badge">✏️ Modified</span>}
                  </label>
                  
                  {isEditing ? (
                    <div className="input-with-unit">
                      <input
                        type="number"
                        step="any"
                        value={currentValue}
                        onChange={(e) => handleParameterChange(stageId, fieldKey, e.target.value)}
                        className="parameter-input"
                      />
                      {unit && <span className="inline-unit">{unit}</span>}
                    </div>
                  ) : (
                    <div className="parameter-display">
                      <span className={`parameter-value ${modified ? 'modified' : ''}`}>
                        {currentValue}
                      </span>
                      {unit && <span className="display-unit">{unit}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2> Review & Calculate</h2>
        <p>Review and adjust parameters before calculation</p>
      </div>

      <div className="review-summary">
        {/* Basic Info Card */}
        <div className="summary-card">
          <h3>📋 Scenario Information</h3>
          <div className="summary-item">
            <span className="summary-label">Name:</span>
            <span className="summary-value">{data.name || 'Not set'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Facility:</span>
            <span className="summary-value">{data.facility || 'Not set'}</span>
          </div>
          {data.comments && (
            <div className="summary-item">
              <span className="summary-label">Comments:</span>
              <span className="summary-value">{data.comments}</span>
            </div>
          )}
        </div>

        {/* Stage Parameters - Editable */}
        {stageSteps.map(step => renderStageParameters(step))}
      </div>
    </div>
  );
};

export default CreateScenarioWizard;
